import dayjs from 'dayjs';

/**
 * Helper function to extract data from termsData.
 * Supports both nested (old: parties.tenant_name) and flat (new: tenant_name) structures.
 */
export const extractValue = (termsData: any, path: string): string => {
  if (!termsData) return '-';

  // First try flat structure (new format)
  const flatKey = path.split('.').pop(); // Get last part of path
  if (flatKey && termsData[flatKey] !== undefined && termsData[flatKey] !== null) {
    return String(termsData[flatKey]);
  }

  // Fall back to nested structure (old format)
  const keys = path.split('.');
  let value = termsData;
  for (const key of keys) {
    value = value?.[key];
    if (value === undefined || value === null) return '-';
  }
  return value || '-';
};

/**
 * Format document type for display.
 * Handles uppercase abbreviations and capitalizes words.
 */
export const formatDocumentType = (type: string | null): string => {
  if (!type || type === '-') return '-';

  // Common document type abbreviations that should be uppercase
  const upperCaseTypes = ['PSA', 'LOI', 'NDA'];
  if (upperCaseTypes.includes(type.toUpperCase())) {
    return type.toUpperCase();
  }

  // Capitalize first letter of each word
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Format currency values for display.
 */
export const formatCurrency = (value: any): string => {
  if (value === '-' || value === null || value === undefined) return '-';
  const numValue = Number(value);
  if (isNaN(numValue)) return '-';
  return `$${numValue.toLocaleString()}`;
};

/**
 * Format currency with decimals for display.
 */
export const formatCurrencyWithDecimals = (value: any): string => {
  if (value === '-' || value === null || value === undefined) return '-';
  const numValue = Number(value);
  if (isNaN(numValue)) return '-';
  return `$${numValue.toFixed(2)}`;
};

/**
 * Format date values for display.
 *
 * Date-only strings ("YYYY-MM-DD") are parsed as LOCAL midnight rather than
 * UTC midnight. Passing a bare date-only string to dayjs()/Date() interprets it
 * as UTC, which then renders as the previous day for users west of UTC
 * (e.g. "2026-01-01" -> "Dec 31, 2025" in US timezones). Appending a local time
 * component keeps the calendar date intact regardless of the viewer's timezone.
 */
export const formatDate = (value: any): string => {
  if (value === '-' || value === null || value === undefined) return '-';

  const localValue =
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? `${value}T00:00:00`
      : value;

  return dayjs(localValue).format('MMM D, YYYY');
};

/**
 * Format boolean values for display.
 */
export const formatBoolean = (value: any): string => {
  if (value === 'true' || value === true) return 'Yes';
  if (value === 'false' || value === false) return 'No';
  return '-';
};

/**
 * Format number values for display (no currency symbol).
 */
export const formatNumber = (value: any): string => {
  if (value === '-' || value === null || value === undefined) return '-';
  const numValue = Number(value);
  if (isNaN(numValue)) return '-';
  return numValue.toLocaleString();
};

/**
 * Format plain text values, returning '-' for empty values.
 */
export const formatText = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};
